<template>
  <div class="post-creation-form">
    <!-- Compact view (initial state) -->
    <div v-if="!expanded" class="compact-form" @click="expand">
      <div class="compact-content">
        <img 
          :src="currentUserProfilePic" 
          alt="Your avatar" 
          class="avatar" 
          @error="handleImageError"
        />
        <div class="compact-placeholder">Share an update with the community...</div>
        <div class="compact-icons">
          <span class="photo-icon" title="Add photos">📷</span>
        </div>
      </div>
    </div>

    <!-- Expanded form -->
    <div v-else class="expanded-form">
      <div class="expanded-header">
        <img 
          :src="currentUserProfilePic" 
          alt="Your avatar" 
          class="avatar" 
          @error="handleImageError"
        />
        <span class="user-name">{{ currentUserName }}</span>
        <button @click="collapse" class="close-btn" type="button">×</button>
      </div>

      <form @submit.prevent="createPost">
        <input
          v-model="postTitle"
          type="text"
          placeholder="Post title (e.g., Lost cat, Community event, Traffic update)"
          class="post-title-input"
          :disabled="submitting"
          maxlength="100"
        />

        <textarea
          ref="postTextarea"
          v-model="postContent"
          placeholder="What's happening in your neighborhood?"
          class="post-textarea"
          rows="4"
          :disabled="submitting"
        ></textarea>

        <!-- Image Upload -->
        <PostImageUpload
          ref="imageUpload"
          @images-uploaded="handleImagesUploaded"
          @upload-progress="handleUploadProgress"
          @upload-error="handleUploadError"
        />

        <div class="form-actions">
          <div class="action-buttons">
            <button 
              type="submit"
              :disabled="!canSubmit || submitting"
              class="submit-btn"
            >
              {{ submitting ? 'Posting...' : 'Share Post' }}
            </button>
            <button 
              type="button"
              @click="collapse"
              class="cancel-btn"
            >
              Cancel
            </button>
          </div>
          
          <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <span class="progress-text">{{ Math.round(uploadProgress) }}% uploaded</span>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import PostImageUpload from './PostImageUpload.vue';
import { usePostManagement } from '../../composables/usePostManagement.js';
import { useKeyboardManagement } from '../../composables/useKeyboardManagement.js';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { getUserName, getUserPicture } from '../../utils/userUtils.js';
import { handleImageError } from '../../utils/imageUtils.js';
import { updatePostImageMetadata } from '../../utils/s3MetadataUtils.js';

export default {
  name: 'PostCreationForm',
  components: {
    PostImageUpload
  },
  setup() {
    // Mobile keyboard management
    useKeyboardManagement();

    return {};
  },
  props: {
    neighborhood: {
      type: String,
      default: null
    }
  },
  emits: ['post-created', 'show-auth'],
  data() {
    return {
      expanded: false,
      postTitle: '',
      postContent: '',
      submitting: false,
      uploadProgress: 0,
      uploadedImages: [],
      currentUser: null,
      currentUserName: '',
      currentUserProfilePic: '/images/default-avatar-silhouette.svg'
    };
  },
  async mounted() {
    await this.loadCurrentUser();
  },
  computed: {
    canSubmit() {
      return this.postTitle.trim().length > 0 && this.postContent.trim().length > 0 && !this.submitting;
    }
  },
  methods: {
    async loadCurrentUser() {
      try {
        this.currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        this.currentUserName = getUserName({ attributes });

        const profilePicResult = getUserPicture({ attributes });
        if (profilePicResult && typeof profilePicResult.then === 'function') {
          this.currentUserProfilePic = await profilePicResult;
        } else {
          this.currentUserProfilePic = profilePicResult || '/images/default-avatar-silhouette.svg';
        }
      } catch (error) {
        // User is not authenticated - this is fine, they can still view posts
        // Don't emit show-auth here as it would redirect them away from viewing content
        console.log('User not authenticated');
        this.currentUser = null;
      }
    },

    expand() {
      // If user is not authenticated, prompt them to sign in
      if (!this.currentUser) {
        this.$emit('show-auth');
        return;
      }

      this.expanded = true;
      this.$nextTick(() => {
        this.$refs.postTextarea?.focus();
      });
    },

    collapse() {
      this.expanded = false;
      this.postTitle = '';
      this.postContent = '';
      this.uploadedImages = [];
      this.uploadProgress = 0;
      if (this.$refs.imageUpload) {
        this.$refs.imageUpload.clearImages();
      }
    },

    async createPost() {
      if (!this.canSubmit) return;

      try {
        this.submitting = true;
        
        // First, upload any images if they exist
        let uploadedImageData = [];
        if (this.$refs.imageUpload && this.$refs.imageUpload.hasImages()) {
          uploadedImageData = await this.$refs.imageUpload.uploadAllImages();
        }
        
        const postManager = usePostManagement();
        
        // Create post data
        const postData = {
          content: this.postContent.trim(),
          title: this.postTitle.trim(),
          neighborhood: this.neighborhood
        };

        // Create the post
        const createdPost = await postManager.createPost(postData);
        
        // If there are images, create PostImage records in DynamoDB
        let postImageRecords = [];
        if (uploadedImageData.length > 0) {
          postImageRecords = await this.createPostImages(createdPost.id, uploadedImageData);
        }

        // Create the complete post object with images for immediate display
        const postWithImages = {
          ...createdPost,
          images: {
            items: postImageRecords
          }
        };

        // Emit success and reset form
        this.$emit('post-created', postWithImages);
        this.collapse();
        
      } catch (error) {
        console.error('Error creating post:', error);
        // User will see submit button re-enabled and form still open
      } finally {
        this.submitting = false;
      }
    },

    async createPostImages(postId, imageDataArray) {
      const createdImages = [];
      
      try {
        const { useAmplifyClient } = await import('../../composables/useAmplifyClient.js');
        const { authenticatedClient, getUserInfo } = useAmplifyClient();
        
        // Get current user for ownership
        const { user } = await getUserInfo();
        if (!user) {
          throw new Error('Must be authenticated to create post images');
        }

        for (let i = 0; i < imageDataArray.length; i++) {
          const imageData = imageDataArray[i];
          
          const postImageData = {
            postId: postId,
            originalUrl: imageData.originalUrl,
            mediumUrl: imageData.mediumUrl,
            thumbnailUrl: imageData.thumbnailUrl,
            fileName: imageData.fileName,
            fileSize: imageData.fileSize,
            mimeType: imageData.mimeType,
            width: imageData.width,
            height: imageData.height,
            uploadedBy: user.userId || user.username,
            owner: user.userId || user.username, // Required for authorization
            order: i
          };


          const result = await authenticatedClient.graphql({
            query: `
              mutation CreatePostImage($input: CreatePostImageInput!) {
                createPostImage(input: $input) {
                  id
                  postId
                  originalUrl
                  mediumUrl
                  thumbnailUrl
                  fileName
                  fileSize
                  mimeType
                  width
                  height
                  uploadedBy
                  order
                }
              }
            `,
            variables: {
              input: postImageData
            }
          });
          
          const createdImage = result.data.createPostImage;
          createdImages.push(createdImage);

          // Update S3 metadata with PostImage ID for efficient content moderation
          await this.updateImageMetadata(imageData, createdImage.id);
        }
        
        return createdImages;
      } catch (error) {
        console.error('Error creating post images:', error);
        // Don't fail the whole post creation for image linking issues, but log the error
        // Post was created successfully, just without the images
        return []; // Return empty array so the post still displays without images
      }
    },

    async updateImageMetadata(imageData, postImageId) {
      try {
        // Update S3 metadata for all image sizes with the PostImage ID
        await updatePostImageMetadata({
          thumbnailUrl: imageData.thumbnailUrl,
          mediumUrl: imageData.mediumUrl,
          originalUrl: imageData.originalUrl
        }, postImageId);
      } catch (error) {
        console.error('Failed to update S3 metadata:', error);
        // Don't fail the main flow for metadata update issues
      }
    },

    handleImagesUploaded(images) {
      this.uploadedImages = images;
    },

    handleUploadProgress(progress) {
      this.uploadProgress = progress;
    },

    handleUploadError(error) {
      console.error('Image upload error:', error);
      // Silent fail - user will see images didn't upload
    },

    handleImageError
  }
};
</script>

<style scoped>
.post-creation-form {
  background: white;
  border-radius: 8px;
  border: 1px solid #ddd;
  overflow: hidden;
}

.compact-form {
  padding: 15px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.compact-form:hover {
  background-color: #f8f9fa;
}

.compact-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.compact-placeholder {
  flex: 1;
  color: #666;
  font-size: 16px;
}

.compact-icons {
  color: #666;
  font-size: 18px;
}

.expanded-form {
  padding: 20px;
}

.expanded-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.user-name {
  flex: 1;
  margin-left: 12px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #666;
}

.post-title-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  font-size: 16px;
  font-family: inherit;
  margin-bottom: 10px;
  font-weight: 600;
}

.post-title-input:focus {
  outline: none;
  border-color: #007bff;
}

.post-title-input::placeholder {
  font-weight: 400;
  color: #999;
}

.post-textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 15px;
}

.post-textarea:focus {
  outline: none;
  border-color: #007bff;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.submit-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.submit-btn:hover:not(:disabled) {
  background: #0056b3;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-btn:hover {
  background: #5a6268;
}

.upload-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar {
  width: 100px;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
}
</style>